import React from "react";

import styled from "styled-components/macro";

import { useSignups } from "Signups/state.js";
import { AnimatePresence, motion } from "framer-motion";

import { SquareCheck } from "UI/FnrEnrichedCheckbox";
import FnrIcon from "UI/FnrIcon";
import { colors } from "UI/colors";

import useHovering from "Utilities/Hooks/useHovering";
import { isTouchDevice } from "Utilities/isTouchDevice";

import PaymentStatusPill from "./PaymentStatusPill";
import RespondentEntryExtendedFields from "./RespondentEntryExtendedFields";
import { useIsSignedUpForAnyItem } from "./utils/useIsSignedUpForAnyItem";

export default function RespondentEntryUser({
  person,
  setChecked,
  signupItem,
  userExtendedFields,
  hasAnyFields,
}) {
  const [state, dispatch] = useSignups();
  const [isHovering, hoverProps] = useHovering();
  const isCurrentlySignedUp = useIsSignedUpForAnyItem();
  const {
    isChecked,
    userID,
    isDisabled,
    currentlySignedUpItemID,
    isAnonymousUser,
  } = person;

  // const { extendedFields } = signupItem;
  const isLoggedIn = state.user.ID === userID;
  const isPaid =
    person.paymentStatus === "Paid" || person.paymentStatus === "MarkedAsPaid";
  const isUnclickable = isPaid && isChecked;
  const isHoveringOrOnTouchDevice = isHovering || isTouchDevice();

  const handleClick = () => {
    if (isDisabled) return;
    if (isUnclickable) return;
    setChecked(userID, !isChecked);
  };

  const getDisplayName = () => {
    /*
    a person may have an empty string person.name, a valid person.name,
    or a person.fname & person.lname
    */
    const shouldUsePlaceholder = person.name && !person.name.trim();

    if (shouldUsePlaceholder) return "(No name provided)";
    else if (person.name) return person.name;
    else return `${person.fname} ${person.lname}`;
  };

  const getItemDisabledItemTitle = () => {
    if (!currentlySignedUpItemID) return "another item.";

    const title = state.currentSignupList.signupItems.find(
      (item) => String(item.ID) === String(currentlySignedUpItemID)
    )?.title;

    return title || "another item.";
  };

  const clearAnonymousUser = () => {
    const { name, emailOrPhone } = person;
    dispatch({
      type: "anonymousUserClearedData",
      payload: {
        name: name || "",
        emailOrPhone: emailOrPhone || "",
      },
    });
    dispatch({
      type: "user",
      payload: null,
    });
  };

  const handleLogoutClick = () => {
    fetch("/logout", { method: "POST" });

    setTimeout(() => {
      // added a setTimeout due to reports that sometimes logout didn't happen
      // (though I haven't been able to personally reproduce)
      window.location.reload();
    }, 500);
  };

  return (
    <Wrapper {...hoverProps}>
      <Clickable
        onClick={handleClick}
        role="button"
        isDisabled={isDisabled}
        isUnclickable={isUnclickable}
        isAnonymousUser={isAnonymousUser}
      >
        <CheckboxWrapper isDisabled={isDisabled} isUnclickable={isUnclickable}>
          <SquareCheck checked={isChecked} circle color="#278dc1" />
        </CheckboxWrapper>
        <NameWrapper>
          <Name
            data-cy="public-signup-respondent-name"
            animate={{ fontSize: isChecked && hasAnyFields ? "24px" : "16px" }}
          >
            {getDisplayName()}
            <PaymentStatusPill
              paymentStatus={person.paymentStatus}
              signupItem={signupItem}
            />
          </Name>
          {isDisabled ? (
            <UserInfo>
              Currently signed up for {getItemDisabledItemTitle()}
            </UserInfo>
          ) : null}
          {isAnonymousUser && !isCurrentlySignedUp ? (
            <UserInfo>
              {person.emailOrPhone ? person.emailOrPhone : ""}
            </UserInfo>
          ) : null}
        </NameWrapper>
        {isAnonymousUser &&
        isHoveringOrOnTouchDevice &&
        !isCurrentlySignedUp ? (
          <EditButton onClick={clearAnonymousUser} role="button">
            <FnrIcon fad fa-pencil />
          </EditButton>
        ) : null}
        {isLoggedIn && isHoveringOrOnTouchDevice ? (
          <NotYouButton onClick={handleLogoutClick} role="button">
            Not You?
          </NotYouButton>
        ) : null}
      </Clickable>
      <AnimatePresence>
        {isChecked && userExtendedFields.length > 0 && (
          <RespondentEntryExtendedFields
            respondent={person}
            fields={userExtendedFields}
            signupItem={signupItem}
          />
        )}
      </AnimatePresence>
    </Wrapper>
  );
}

const Wrapper = styled(motion.div)`
  padding: 3px 0 3px 12px;
`;

const Clickable = styled.div`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  min-height: 36px;
  padding-right: 12px;
  opacity: ${({ isDisabled }) => (isDisabled ? "0.7" : "1")};
  cursor: ${({ isDisabled, isUnclickable }) =>
    isDisabled || isUnclickable ? "not-allowed" : "pointer"};
  cursor: ${({ isAnonymousUser }) => (isAnonymousUser ? "default" : null)};
`;

const CheckboxWrapper = styled.div`
  margin-right: 12px;
  flex-shrink: 0;
  margin-top: 1px;
  pointer-events: ${(p) => (p.isUnclickable ? "none" : "auto")};
`;

const Name = styled(motion.div)`
  display: flex;
  align-items: center;
`;

const NameWrapper = styled.div`
  font-weight: 400;
`;

const UserInfo = styled.div`
  opacity: 0.5;
  font-size: 12px;
  margin-top: 0px;
`;

const EditButton = styled.div`
  cursor: pointer;
  margin-left: 10px;
  margin-right: 5px;
  flex-shrink: 0;
  color: #b7b7b7;
  transition: color 200ms;

  &:hover {
    color: ${colors.fnBlue};
  }
`;

const NotYouButton = styled(EditButton)`
  font-size: 13px;
  font-weight: 400;
`;

// const NoName = styled.span`
//   color: #585858;
// `;
